import React from 'react';

import { Autocomplete, TextField } from '@mui/material';

import { useGetLensTeamPeople } from '~/hooks/apis/reporting/useLensSpaces';
import { EmployeeUserOption, useGetEmployeeUsers } from '~/hooks/apis/users/useGetEmployeeUsers';
import useDebounced from '~/hooks/useDebounced';

function oneAccountPerEmail(users: Array<EmployeeUserOption>): Array<EmployeeUserOption> {
  const seen = new Set<string>();
  return users.filter((user) => {
    const email = user.email.toLowerCase();
    if (seen.has(email)) {
      return false;
    }
    seen.add(email);
    return true;
  });
}

const MIN_SEARCH_LENGTH = 3;
const SEARCH_DELAY_MS = 300;

interface Props {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: string | null;
  excludedEmails: Array<string>;
  onSubmit?: () => void;
  source?: 'employee' | 'team';
}

type Results = { data?: Array<EmployeeUserOption>; isError: boolean; isFetching: boolean };

function RecipientAutocomplete({
  value,
  onChange,
  disabled,
  error,
  excludedEmails,
  onSubmit,
  results,
  enabled,
}: Props & { results: Results; enabled: boolean }) {
  const excluded = new Set(excludedEmails.map((email) => email.toLowerCase()));
  const options = enabled
    ? oneAccountPerEmail((results.data ?? []).filter((user) => !excluded.has(user.email.toLowerCase())))
    : [];
  const searchUnavailable =
    results.isError && enabled ? 'People search is unavailable. Enter a complete work email instead.' : null;
  const helperText = [searchUnavailable, error].filter(Boolean).join(' ');

  return (
    <Autocomplete
      freeSolo
      fullWidth
      disabled={disabled}
      options={options}
      value={value}
      inputValue={value}
      loading={enabled && results.isFetching}
      filterOptions={(entries) => entries}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.email)}
      onInputChange={(_, next, reason) => {
        if (reason === 'input' || reason === 'clear') {
          onChange(next);
        }
      }}
      onChange={(_, next) => onChange(typeof next === 'string' ? next : (next?.email ?? ''))}
      renderOption={(props, option) => (
        <li {...props} key={option.id_account}>
          {option.full_name} ({option.email})
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label="Name or email"
          error={Boolean(error)}
          helperText={helperText}
          inputProps={{
            ...params.inputProps,
            onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
              params.inputProps.onKeyDown?.(event);
              if (!onSubmit || event.key !== 'Enter' || event.defaultPrevented || event.nativeEvent.isComposing) {
                return;
              }
              event.preventDefault();
              onSubmit();
            },
          }}
        />
      )}
    />
  );
}

function EmployeeRecipientInput(props: Props) {
  const search = useDebounced(props.value.trim(), SEARCH_DELAY_MS);
  const enabled = !props.disabled && search.length >= MIN_SEARCH_LENGTH;
  const users = useGetEmployeeUsers(search, enabled);
  return <RecipientAutocomplete {...props} results={users} enabled={enabled} />;
}

function TeamRecipientInput(props: Props) {
  const search = useDebounced(props.value.trim(), SEARCH_DELAY_MS);
  const enabled = !props.disabled && search.length >= MIN_SEARCH_LENGTH;
  const people = useGetLensTeamPeople(search, enabled);
  const results: Results = {
    data: people.data?.map((person) => ({ email: person.email, full_name: person.name, id_account: person.email })),
    isError: people.isError,
    isFetching: people.isFetching,
  };
  return <RecipientAutocomplete {...props} results={results} enabled={enabled} />;
}

export default function LensRecipientInput(props: Props) {
  return props.source === 'team' ? <TeamRecipientInput {...props} /> : <EmployeeRecipientInput {...props} />;
}
